import {
  CheckIcon,
  ChevronsUpDown,
  LogOut,
  StoreIcon,
} from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar"
import { SIDEBAR_LINKS, VERSION } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { usePermission } from "@/contexts/permission"
import { useWhitelabel } from "@/contexts/whitelabel"
import { useWhiteLabels } from "@/hooks"

import BrandLogo from "../commons/app-brand-logo"
import { AppSidebarMenu } from "./app-sidebar-menu"

export const AppSidebar = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    authData: session,
    logout,
    selectedWhiteLabel,
    setSelectedWhiteLabel,
  } = useAuth()
  const isAuthenticated = !!session?.accessToken
  const { data: whiteLabelsData } = useWhiteLabels({
    enabled: isAuthenticated,
  })
  const { configuration } = useWhitelabel()
  const selectedWhiteLabelName =
    selectedWhiteLabel?.name || t("sidebar.allStores")
  const brand = import.meta.env.VITE_BRAND ?? "pay2b"
  const { isSuperAdmin, isAdmin } = usePermission()
  // Staff Pay2B (superAdmin) enxerga tudo; admin de merchant
  // enxerga os links requireAdmin (hierarquia superAdmin ⊃ admin).
  const sidebarLinks = SIDEBAR_LINKS.filter((link) => {
    if (link.requireSuperAdmin) return isSuperAdmin
    if (link.requireAdmin) return isAdmin || isSuperAdmin
    return true
  })
  const getUserInitials = (name?: string) => {
    if (!name) return "U"
    const parts = name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }
  const handleSelectWhiteLabel = useCallback(
    (whitelabel: WhitelabelType) => {
      setSelectedWhiteLabel(whitelabel)
      const params = new URLSearchParams(
        searchParams.toString()
      )
      if (whitelabel.uuid) {
        params.set("whiteLabelId", whitelabel.uuid)
      } else {
        params.delete("whiteLabelId")
      }
      params.set("refetch", String(Date.now()))
      navigate(`?${params.toString()}`)
    },
    [navigate, searchParams, setSelectedWhiteLabel]
  )
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-border/40 pb-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="rounded-lg transition-colors hover:bg-sidebar-accent"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <BrandLogo
                      brand={brand}
                      className="h-5 w-5"
                      customLogo={
                        configuration?.merchant?.logo
                      }
                      customAlt={
                        configuration?.merchant?.fantasyName
                      }
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-0.5 text-left">
                    <span className="truncate text-sm font-semibold">
                      {selectedWhiteLabelName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {t("sidebar.merchantPortal")}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl"
                side="bottom"
                align="start"
                sideOffset={8}
              >
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t("sidebar.selectStore")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {whiteLabelsData?.map((whitelabel) => (
                  <DropdownMenuItem
                    key={whitelabel.uuid}
                    className="flex cursor-pointer items-center gap-2 rounded-lg p-2"
                    onClick={() =>
                      handleSelectWhiteLabel(whitelabel)
                    }
                  >
                    <StoreIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate">
                      {whitelabel.name}
                    </span>
                    {selectedWhiteLabel?.uuid ===
                      whitelabel.uuid && (
                      <CheckIcon className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="py-4">
        <AppSidebarMenu sidebarLinks={sidebarLinks} />
      </SidebarContent>
      <SidebarFooter className="border-t border-border/40 pt-2">
        <div className="flex items-center justify-center px-2 py-1 text-[10px] text-muted-foreground/60 group-data-[state=collapsed]:hidden">
          v{VERSION.PROJECT_VERSION}
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="rounded-lg transition-colors hover:bg-sidebar-accent"
                >
                  <Avatar className="h-8 w-8 rounded-lg bg-primary/10">
                    <AvatarImage
                      src="/avatar.png"
                      alt={session?.user?.fullName}
                    />
                    <AvatarFallback className="rounded-lg bg-primary/10 text-xs font-medium text-primary">
                      {getUserInitials(
                        session?.user?.fullName
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-1 flex-col gap-0.5 text-left">
                    <span className="truncate text-sm font-medium">
                      {session?.user?.fullName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {t("sidebar.administrator")}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl"
                side="top"
                align="start"
                sideOffset={8}
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium">
                      {session?.user?.fullName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Merchant ID:{" "}
                      {session?.user?.merchantId}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="flex cursor-pointer items-center gap-2 rounded-lg p-2 text-destructive focus:text-destructive"
                  onClick={logout}
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t("common.logout")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
